/**
 * D244: big numbers for the idle game. `950`, `1,240`, `10.2K`, `34.5M`, `678B`, and on through the
 * named units up to `T`; from 1e15 the number goes to `2.4e15`. Pure, so the screens and the
 * tests read the same thing. Figures are cut down, never rounded up, so a balance never
 * shows more than the player holds.
 */
import { currencyShort } from '../idle/terms.ts';

/**
 * Short names for each power of a thousand, from a thousand up to a trillion. Past `T` the
 * names (Qa, Qi, Sx and on) are jargon (docs/STYLE.md), so from 1e15 a figure is written
 * as an exponent: `2.4e15`.
 */
export const BIG_UNITS = ['K', 'M', 'B', 'T'] as const;

/** Below this a figure is written in full, `9,999`; from it, shortened, `10K`. */
export const SHORTEN_FROM = 10_000;

/** A whole number with en-GB grouping, cut down rather than rounded. */
function whole(n: number): string {
  return Math.floor(n).toLocaleString('en-GB');
}

/** Cut a value down to `places` decimals and drop a trailing `.0`. */
function cut(n: number, places: number): string {
  const f = 10 ** places;
  const v = Math.floor(n * f + 1e-9) / f;
  return v.toFixed(places).replace(/\.0+$/, '');
}

/**
 * `1234` -> `1,234`, `12345` -> `12.3K`, `123456` -> `123K`. Below ten thousand the whole
 * number, grouped. Negative values keep their sign; anything not finite reads `0`.
 */
export function fmtBig(n: number): string {
  if (!Number.isFinite(n)) return '0';
  if (n < 0) {
    const s = fmtBig(-n);
    return s === '0' ? '0' : `-${s}`;
  }
  if (n < SHORTEN_FROM) return whole(n);
  const tier = Math.floor(Math.log10(n) / 3);
  if (tier > BIG_UNITS.length) {
    const exp = Math.floor(Math.log10(n));
    return `${cut(n / 10 ** exp, 1)}e${exp}`;
  }
  let scaled = n / 1000 ** tier;
  let t = tier;
  // Floating point can leave 999.999...; settle it into the next unit up.
  if (scaled >= 1000) {
    scaled /= 1000;
    t += 1;
  }
  if (scaled < 1) {
    scaled *= 1000;
    t -= 1;
  }
  if (t === 0) return whole(scaled);
  if (t > BIG_UNITS.length) {
    const exp = Math.floor(Math.log10(n));
    return `${cut(n / 10 ** exp, 1)}e${exp}`;
  }
  const unit = BIG_UNITS[t - 1];
  return `${cut(scaled, scaled >= 100 ? 0 : 1)}${unit}`;
}

/**
 * D357: `n` at a fixed unit and decimal count, not trimmed, so two close numbers still read
 * differently: `1.00M`, `1.01M`. Below `SHORTEN_FROM` it is the plain whole number, same as
 * `fmtBig`, since there is no unit to pin decimals to.
 */
export function fmtBigFixed(n: number, decimals: number): string {
  if (!Number.isFinite(n) || n < 0) return '0';
  if (n < SHORTEN_FROM) return whole(n);
  let tier = Math.min(Math.floor(Math.log10(n) / 3), BIG_UNITS.length);
  let scaled = n / 1000 ** tier;
  if (scaled >= 1000) {
    scaled /= 1000;
    tier += 1;
  }
  if (tier > BIG_UNITS.length) {
    const exp = Math.floor(Math.log10(n));
    return `${cut(n / 10 ** exp, 1)}e${exp}`;
  }
  const unit = BIG_UNITS[tier - 1] ?? '';
  const f = 10 ** decimals;
  const v = Math.floor(scaled * f + 1e-9) / f;
  return `${v.toFixed(decimals)}${unit}`;
}

/** Money in the house style: `300c`, `1,240c`, `10.2Kc`, `3.4Mc`. */
export function fmtCoins(n: number): string {
  return `${fmtBig(n)}${currencyShort()}`;
}

/** A gain as it pops: `+5c`, `+12.3Kc`. */
export function fmtGain(n: number): string {
  return `+${fmtCoins(n)}`;
}
